import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { InputSwitch } from 'primereact/inputswitch';
import { Input } from 'src/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  useCreateDesignerCanvasClassMutation,
  useUpdateDesignerCanvasClassMutation,
} from 'src/redux/operations/operations.endpoints';
import type { CanvasClass } from '../types/creative-template.types';
import type { DesignerCanvasClass, DesignerCanvasClassWritePayload } from '../types/designer-exemplar.types';
import { CANVAS_CLASS_OPTIONS } from '../types/creative-template.types';
import { parseDesignerExemplarMutationError, normaliseDesignerCanvasClasses } from '../helpers/designer-exemplar.helpers';
import { SchedulerDsButton } from './scheduler-ds-button';

interface DesignerCanvasClassTableProps {
  canvasClasses: DesignerCanvasClass[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

type EditableCanvasClass = DesignerCanvasClassWritePayload & { slug: CanvasClass };

const EMPTY_CANVAS_CLASS: EditableCanvasClass = {
  slug: 'panel_square',
  display_name: '',
  reference_width: 1080,
  reference_height: 1080,
  tolerance: 0.05,
  active: true,
};

export function DesignerCanvasClassTable({
  canvasClasses,
  isLoading,
  isError,
}: DesignerCanvasClassTableProps) {
  const [draft, setDraft] = useState<EditableCanvasClass | null>(null);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<DesignerCanvasClassWritePayload>({});
  const [error, setError] = useState<string | null>(null);

  const [createCanvasClass, { isLoading: isCreating }] = useCreateDesignerCanvasClassMutation();
  const [updateCanvasClass, { isLoading: isUpdating }] = useUpdateDesignerCanvasClassMutation();

  const availableCreateSlugs = useMemo(() => {
    const existing = new Set(normaliseDesignerCanvasClasses(canvasClasses).map((entry) => entry.slug));
    return CANVAS_CLASS_OPTIONS.filter((slug) => !existing.has(slug));
  }, [canvasClasses]);

  const handleCreate = async() => {
    if (!draft) {
      return;
    }
    try {
      await createCanvasClass(draft).unwrap();
      setDraft(null);
      setError(null);
    } catch (mutationError) {
      setError(parseDesignerExemplarMutationError(mutationError));
    }
  };

  const handleSaveEdit = async(slug: string) => {
    try {
      await updateCanvasClass({ slug, body: editValues }).unwrap();
      setEditingSlug(null);
      setEditValues({});
      setError(null);
    } catch (mutationError) {
      setError(parseDesignerExemplarMutationError(mutationError));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-gray-500" role="status">
        <Loader2 className="size-4 animate-spin text-gray-500" />
        Loading canvas classes…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700" role="alert">
        Could not load canvas classes.
      </div>
    );
  }

  return (
    <section className="space-y-4 rounded-md border border-gray-300 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-black">Canvas classes</h3>
          <p className="text-xs text-gray-500">
            Reference dimensions and tolerance per class. Slug is fixed after creation.
          </p>
        </div>
        {availableCreateSlugs.length > 0 ? (
          <SchedulerDsButton
            type="button"
            size="sm"
            onClick={() => {
              setDraft({
                ...EMPTY_CANVAS_CLASS,
                slug: availableCreateSlugs[0],
              });
              setError(null);
            }}
          >
            Add class
          </SchedulerDsButton>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-md border border-red-101 bg-red-101 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      ) : null}

      {draft ? (
        <div className="grid gap-3 rounded-md border border-gray-300 bg-gray-50 p-4 md:grid-cols-3">
          <label className="space-y-1 text-sm">
            <span className="font-medium text-gray-700">Slug</span>
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              value={draft.slug}
              onChange={(event) => setDraft({ ...draft, slug: event.target.value as CanvasClass })}
            >
              {availableCreateSlugs.map((slug) => (
                <option key={slug} value={slug}>
                  {slug}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-gray-700">Display name</span>
            <Input
              value={draft.display_name ?? ''}
              onChange={(event) => setDraft({ ...draft, display_name: event.target.value })}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-gray-700">Tolerance</span>
            <Input
              type="number"
              step="0.01"
              value={draft.tolerance ?? 0}
              onChange={(event) => setDraft({ ...draft, tolerance: Number(event.target.value) })}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-gray-700">Reference width</span>
            <Input
              type="number"
              value={draft.reference_width ?? 0}
              onChange={(event) => setDraft({ ...draft, reference_width: Number(event.target.value) })}
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-gray-700">Reference height</span>
            <Input
              type="number"
              value={draft.reference_height ?? 0}
              onChange={(event) => setDraft({ ...draft, reference_height: Number(event.target.value) })}
            />
          </label>
          <div className="flex items-center gap-3">
            <InputSwitch
              checked={draft.active ?? true}
              onChange={(event) => setDraft({ ...draft, active: event.value })}
            />
            <span className="text-sm text-gray-700">Active</span>
          </div>
          <div className="flex gap-2 md:col-span-3">
            <SchedulerDsButton type="button" disabled={isCreating} onClick={() => void handleCreate()}>
              {isCreating ? <Loader2 className="size-4 animate-spin" /> : null}
              Create
            </SchedulerDsButton>
            <SchedulerDsButton type="button" variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </SchedulerDsButton>
          </div>
        </div>
      ) : null}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Slug</TableHead>
            <TableHead>Display name</TableHead>
            <TableHead>Dimensions</TableHead>
            <TableHead>Tolerance</TableHead>
            <TableHead>Active</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {(normaliseDesignerCanvasClasses(canvasClasses)).map((entry) => {
            const isEditing = editingSlug === entry.slug;
            return (
              <TableRow key={entry.slug}>
                <TableCell className="font-medium">{entry.slug}</TableCell>
                <TableCell>
                  {isEditing ? (
                    <Input
                      value={editValues.display_name ?? entry.display_name}
                      onChange={(event) => setEditValues({ ...editValues, display_name: event.target.value })}
                    />
                  ) : (
                    entry.display_name
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={editValues.reference_width ?? entry.reference_width}
                        onChange={(event) => setEditValues({
                          ...editValues,
                          reference_width: Number(event.target.value),
                        })}
                      />
                      <Input
                        type="number"
                        value={editValues.reference_height ?? entry.reference_height}
                        onChange={(event) => setEditValues({
                          ...editValues,
                          reference_height: Number(event.target.value),
                        })}
                      />
                    </div>
                  ) : (
                    `${entry.reference_width} × ${entry.reference_height}`
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <Input
                      type="number"
                      step="0.01"
                      value={editValues.tolerance ?? entry.tolerance}
                      onChange={(event) => setEditValues({
                        ...editValues,
                        tolerance: Number(event.target.value),
                      })}
                    />
                  ) : (
                    entry.tolerance
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <InputSwitch
                      checked={editValues.active ?? entry.active}
                      onChange={(event) => setEditValues({ ...editValues, active: event.value })}
                    />
                  ) : (
                    entry.active ? 'Yes' : 'No'
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <div className="flex gap-2">
                      <SchedulerDsButton
                        type="button"
                        size="sm"
                        disabled={isUpdating}
                        onClick={() => void handleSaveEdit(entry.slug)}
                      >
                        Save
                      </SchedulerDsButton>
                      <SchedulerDsButton
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditingSlug(null);
                          setEditValues({});
                        }}
                      >
                        Cancel
                      </SchedulerDsButton>
                    </div>
                  ) : (
                    <SchedulerDsButton
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEditingSlug(entry.slug);
                        setEditValues({});
                        setError(null);
                      }}
                    >
                      Edit
                    </SchedulerDsButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
}
